import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * AddCircle component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconAddCircle extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${ifDefined(this.iconTitle && 'img')}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11.9995 2.60516C6.81078 2.60516 2.60449 6.81145 2.60449 12.0002C2.60449 17.1889 6.81078 21.3952 11.9995 21.3952C17.1882 21.3952 21.3945 17.1889 21.3945 12.0002C21.3945 6.81145 17.1882 2.60516 11.9995 2.60516ZM1.10449 12.0002C1.10449 5.98302 5.98235 1.10516 11.9995 1.10516C18.0166 1.10516 22.8945 5.98302 22.8945 12.0002C22.8945 18.0173 18.0166 22.8952 11.9995 22.8952C5.98235 22.8952 1.10449 18.0173 1.10449 12.0002Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12.7498 7.31769C12.7498 6.90347 12.414 6.56769 11.9998 6.56769C11.5855 6.56769 11.2498 6.90347 11.2498 7.31769V11.2498H7.31738C6.90317 11.2498 6.56738 11.5856 6.56738 11.9998C6.56738 12.414 6.90317 12.7498 7.31738 12.7498H11.2498V16.6823C11.2498 17.0965 11.5855 17.4323 11.9998 17.4323C12.414 17.4323 12.7498 17.0965 12.7498 16.6823V12.7498H16.682C17.0962 12.7498 17.432 12.414 17.432 11.9998C17.432 11.5856 17.0962 11.2498 16.682 11.2498H12.7498V7.31769Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-add-circle') === undefined) {
  customElements.define('cat-icon-add-circle', BlocksIconAddCircle);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-add-circle': BlocksIconAddCircle;
  }
}
